import React, { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * InlineIntegrityChip — a compact, inline cousin of SystemIntegrityBadge.
 *
 * The global badge already lives in the page chrome (Layout header). This
 * chip is meant to sit *next to financial decisions* — on the Dashboard
 * KPI hero, the P&L header, the Exports header — so operators see truth
 * state where the numbers are, not just in the corner.
 *
 * Same single source: GET /api/accounts/system-integrity.
 * No new endpoints, no new state model, no popover (the global badge
 * already owns the per-check breakdown).
 */
const VISUAL = {
  VALID: { Icon: CheckCircle2, color: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30', label: 'Integrity VALID' },
  PARTIAL: { Icon: AlertTriangle, color: 'text-amber-300 bg-amber-500/10 border-amber-500/30', label: 'Integrity PARTIAL' },
  INVALID: { Icon: XCircle, color: 'text-rose-300 bg-rose-500/10 border-rose-500/30', label: 'Integrity INVALID' },
};

export default function InlineIntegrityChip({ surface = 'unknown', className = '' }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading'); // loading | error | ready

  useEffect(() => {
    let mounted = true;
    const fetchOnce = async () => {
      try {
        const token =
          sessionStorage.getItem('token') ||
          sessionStorage.getItem('access_token') ||
          localStorage.getItem('token') ||
          localStorage.getItem('access_token');
        if (!token) { if (mounted) setState('error'); return; }
        const res = await fetch(`${BACKEND_URL}/api/accounts/system-integrity`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { if (mounted) setState('error'); return; }
        const json = await res.json();
        if (!mounted) return;
        setData(json);
        setState('ready');
      } catch (_e) {
        if (mounted) setState('error');
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 60_000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  if (state === 'loading') {
    return (
      <div
        className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-zinc-800/60 border border-zinc-700/60 text-zinc-400 text-[11px] font-medium', className)}
        data-testid={`inline-integrity-loading-${surface}`}
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>Integrity</span>
      </div>
    );
  }

  if (state === 'error' || !data) {
    return (
      <div
        className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-zinc-800/60 border border-zinc-700/60 text-zinc-500 text-[11px] font-medium', className)}
        data-testid={`inline-integrity-error-${surface}`}
        title="Integrity probe unavailable"
      >
        <AlertTriangle className="w-3 h-3" />
        <span>Integrity ?</span>
      </div>
    );
  }

  const stateKey = data.state || 'PARTIAL';
  const v = VISUAL[stateKey] || VISUAL.PARTIAL;
  const Icon = v.Icon;
  const summary = data.summary || { pass: 0, fail: 0, partial: 0, total: 0 };

  return (
    <div
      className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[11px] font-medium', v.color, className)}
      title={`${v.label} · ${summary.pass}/${summary.total} checks pass`}
      data-testid={`inline-integrity-${stateKey.toLowerCase()}-${surface}`}
    >
      <Icon className="w-3 h-3" />
      <span>{v.label}</span>
      <span className="opacity-70">· {summary.pass}/{summary.total}</span>
    </div>
  );
}
